<script setup lang="tsx">
import { onMounted, ref, watch, type Ref } from 'vue'
import { fetchCustomValues, type CustomValueMap } from '@/services/custom'
import type { CustomFieldDefinition, Response, User } from '@/models/api-generated'
import http from '@/services/http'
import { formatLocale, getDateFormat } from '@/services/utils'
import { CF_Calendar, CF_Checkbox, CF_Select, CF_Text, CF_User } from '@/models/enums-generated'
import UserSelect from '../widgets/UserSelect.vue'
import Datepicker from '@vuepic/vue-datepicker'
import { useTheme } from '../user/useTheme'

const { theme } = useTheme()
const props = defineProps({
	table: {
		type: String,
		required: true,
	},
	rowId: {
		type: Number,
		default: 0,
	},
})

const custom = ref<CustomFieldDefinition[]>([])
const values = ref<CustomValueMap>({})
let input = ''

onMounted(async () => {
	const { data } = await http.get<Response<CustomFieldDefinition[]>>(`api/customFields/${props.table}`)
	if (data.data?.length) {
		custom.value.push(...data.data)
		if (props.rowId) values.value = (await fetchCustomValues(props.table, props.rowId)) ?? {}
	}
})

const userRefs: { [defId: number]: Ref<User | undefined> } = {}
function userRef(defId: number, rowId: number) {
	if (!values.value) return ref<never>()

	if (!userRefs[defId]) userRefs[defId] = ref<User | undefined>()

	if (!userRefs[defId].value) {
		const userId = Number.parseInt(values.value[defId][rowId])
		if (userId) {
			http.get<Response<User>>(`api/user/${userId}`).then((a) => {
				userRefs[defId].value = a.data.data
			})
		}

		watch(userRefs[defId], (user) => {
			if (!values.value) return

			if (user) values.value[defId][rowId] = user.id.toString()
			else delete values.value[defId][rowId]
		})
	}

	return userRefs[defId]
}

function set(definitionId: number) {
	if (!values.value) values.value = {}
	if (!values.value[definitionId]) values.value[definitionId] = {}
}
</script>

<template>
	<template v-if="custom.length && values">
		<div class="row mb-3 text-wrap" v-for="cf in custom" :key="`${cf.id}_${props.rowId}`" :set="input = 'custom-field-' + cf.id">
			<div class="col-md-4">
				<label :for="input" class="form-label">{{ cf.label }}</label>
			</div>
			<div class="col-md-8" :set="set(cf.id)">
				<input v-if="cf.type === CF_Text" type="text" :id="input" class="form-control" v-model="values[cf.id][props.rowId]" />

				<select v-else-if="cf.type === CF_Select" :id="input" class="form-select" v-model="values[cf.id][props.rowId]">
					<option>-</option>
					<option v-for="v in cf.values" :key="v" :value="v">{{ v }}</option>
				</select>

				<div v-else-if="cf.type === CF_Checkbox" class="form-check form-switch form-switch-md">
					<input class="form-check-input" type="checkbox" :id="input" v-model="values[cf.id][props.rowId]" true-value="1" false-value="" />
				</div>

				<Datepicker
					v-else-if="cf.type === CF_Calendar"
					v-model="values[cf.id][props.rowId]"
					:id="input"
					:teleport="true"
					:clearable="true"
					:enable-time-picker="false"
					:format="getDateFormat()"
					:format-locale="formatLocale()"
					:select-text="$t('misc.select')"
					:cancel-text="$t('misc.cancel')"
					text-input
					class="form-control input-box"
					:dark="theme === 'dark'"
				/>
				<UserSelect v-else-if="cf.type === CF_User" v-model="userRef(cf.id, props.rowId).value"></UserSelect>
			</div>
		</div>
	</template>

	<input type="hidden" name="legacy_custom" :value="JSON.stringify(values)" />
</template>
